import React, { useState, useEffect, useLayoutEffect } from 'react';
import Search from '@ingka/search';
import styled from 'styled-components';
import { useTranslation } from '@ikea-customer-self-serve/gatsby-plugin-translations';
import Loading, { LoadingBall } from '@ingka/loading';
import { useCart } from '../contexts/Cart';
import { useMedia } from '../hooks/useMedia';
import { DisplaySearchItems } from './DisplaySearchItems';
import { Onboarding } from './Onboarding';
import { SparePart } from '../interfaces/Item';
import { SelectedItem } from './ItemDisplay';
import { useSearch } from '../hooks/useSearch';
import { getURLParameters } from '../helpers/url';
import greenDayImage from '../images/onboarding/greenday.png';
import AspectRatioImage from '@ingka/aspect-ratio-image';
import TMarkUp from './TMarkUp';
import { sparePartService } from '../services';
import { removeEmojis } from '../utils/emojiFilter';

type HTMLElementEvent<T extends HTMLElement> = Event & {
  target: T;
};

const StyledSearchField = styled.div`
  width: 600px;
  margin-bottom: 40px;
  overflow: hidden;
`;

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
`;

const OnDesktopImage = styled.div`
  margin-bottom: 46px;
  width: 600px;
  margin-left: auto;
  position: relative;
  ${`@media (max-width: 900px) {
   display: none;
 }`}
`;

const OnTabletImage = styled.div`
  margin-bottom: 46px;
  margin-top: 24px;
  max-width: 578px;
`;

const DisplaySearchItemsWrapper = styled.ul`
  list-style: none;
  max-width: 800px;
`;

const StyledSearch = styled(Search)`
  min-width: 200px;
  margin-top: 20px;
  margin-right: 20px;
  foreground-color: #525252;
  background-color: #f3f3f3;

  button.search__clear {
    position: absolute;
  }
`;

const OnboardingDescription = styled.p`
  max-width: 670px;
  padding-right: 18px;
`;

const SearchResults = (): JSX.Element => {
  const { t } = useTranslation();
  const CartContext = useCart();
  const refInput = React.useRef<HTMLDivElement>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { isDesktop, isMobile, isTablet } = useMedia();
  const [NoDataFound, setNoDataFound] = useState(false);

  const initialArtId = getURLParameters('art');
  const initialItemId = getURLParameters('item');
  const [
    { loading, search, loadingParameter, items, componentData, selectedItem },
    { setSearch, setSelectedItem },
  ] = useSearch(initialArtId as string, initialItemId as string);
  const [itemFamily, setItemFamily] = useState<SparePart[]>([]);

  const onChangeSearch =
    (searchTerm: string | void) =>
    (e: HTMLElementEvent<HTMLInputElement>): void => {
      const rawSearch = searchTerm || e.target.value.trim();
      const newSearch = removeEmojis(rawSearch);
      setSearch(newSearch);
      setSelectedItem(undefined);
    };

  useEffect(() => {
    const searchFamily = async (id: string) => {
      const itemFamilyData = await sparePartService.searchSparePartsFamily(id);
      setItemFamily(itemFamilyData);
    };

    if (selectedItem) {
      const { id } = selectedItem;
      setSearch(`${id}`);
      searchFamily(`${id}`);
      if (refInput.current && selectedItem.status !== 'AVAILABLE') {
        const input = refInput.current.querySelector('input');
        if (input && isDesktop) {
          input.focus();
        } else if (input && isMobile) {
          input.blur();
        }
      }

      if (selectedItem.status === 'AVAILABLE') {
        const quantityDropdown = document.querySelector(
          '.quantity-dropdown select'
        ) as HTMLSelectElement;
        if (quantityDropdown) {
          quantityDropdown.focus();
        }
      }
    }
  }, [selectedItem]);

  useEffect(() => {
    const shouldShow = !(
      selectedItem ||
      search.length ||
      CartContext.state.items.size ||
      items.length ||
      componentData.length
    );
    setShowOnboarding(shouldShow);
    if (
      (!items || items.length === 0) &&
      (!componentData || componentData.length === 0)
    ) {
      setNoDataFound(true);
    } else {
      setNoDataFound(false);
    }
  }, [search, selectedItem, items, componentData, CartContext.state.items]);

  useLayoutEffect(() => {
    if (refInput.current) {
      const input = refInput.current.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  }, []);

  if (loadingParameter) {
    return (
      <Loading
        text={t('loading', {
          loading: t(`spareParts.sparePart`),
        })}
      >
        <LoadingBall />
      </Loading>
    );
  }

  return (
    <>
      <FlexContainer>
        <StyledSearchField ref={refInput}>
          {showOnboarding && (
            <OnboardingDescription>
              <TMarkUp value={t(`spareParts.onboarding.description`)} />
            </OnboardingDescription>
          )}
          {isTablet && showOnboarding && (
            <OnTabletImage>
              <AspectRatioImage ratio="wide" src={greenDayImage} alt="" />
            </OnTabletImage>
          )}
          <StyledSearch
            id="search-field"
            placeholder={t('spareParts.search.searchPartName')}
            clearBtnText={t('spareParts.search.clearButtonText')}
            onChange={onChangeSearch()}
            submitBtnText={t('spareParts.search.search')}
            speak-as="spell-out"
          />
        </StyledSearchField>
        {isDesktop && showOnboarding && (
          <OnDesktopImage>
            <AspectRatioImage ratio="wide" src={greenDayImage} alt="" />
          </OnDesktopImage>
        )}
      </FlexContainer>
      <div aria-live="polite" aria-atomic="true">
        {showOnboarding ? (
          <Onboarding />
        ) : (
          <DisplaySearchItemsWrapper>
            {selectedItem ? (
              <SelectedItem
                item={selectedItem as SparePart}
                itemFamily={itemFamily}
                onClickItem={setSelectedItem}
                onChangeSearch={onChangeSearch}
                setSearch={setSearch}
                onSetSelectedItem={setSelectedItem}
                refInput={refInput}
              />
            ) : (
              <>
                {NoDataFound ? (
                  <p> {t('error.noDataFound')}</p>
                ) : (
                  <DisplaySearchItems
                    loading={loading}
                    items={items}
                    componentData={componentData}
                    search={search}
                    itemFamily={itemFamily}
                    onClickItem={setSelectedItem}
                    onChangeSearch={onChangeSearch}
                    setSearch={setSearch}
                    onSetSelectedItem={setSelectedItem}
                    refInput={refInput}
                  />
                )}
                {NoDataFound === true ? '' : <p>{t('error.noDataFound')}</p>}
              </>
            )}
          </DisplaySearchItemsWrapper>
        )}
      </div>
    </>
  );
};
export default SearchResults;
